import React from 'react';
import PropTypes from 'prop-types';
import { IconCheck, IconPlus } from '@americanexpress/dls-icons';
import { useIntl } from 'react-intl';
import { useReactlytics } from '@americanexpress/reactlytics';
import styles from '../styles/HotelSlider.scss';
import { getCityAndLocation } from '../utils/getCityAndLocation';

const getPromotionBackground = (promotion) => {
  switch (promotion) {
    case 'FHR':
      return styles.fineHotelsCard;
    case 'THC':
      return 'dls-deep-blue-bg dls-white';
    /* istanbul ignore next */
    default:
      return 'dls-black';
  }
};

export const HotelCard = ({ hotel, isSelected, selectHotel }) => {
  const intl = useIntl();
  const { track } = useReactlytics();
  const {
    hotelId, address, name, totalPrice, programRate, media,
  } = hotel;

  const { programName, programDescription } = programRate || {};
  const trackableProgramName = programName ? `-${programName}` : '';

  const { title, url } = media[0] || {};

  const { city, location } = getCityAndLocation(address);

  const formatCurrency = (price) => {
    if (price == null || Number.isNaN(price)) return '';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const deeplinkTest = () => {
    track({
      event: {
        identifier: 'axp - WishlistBuilder - Chat',
        element: `RecommendationTile-${hotelId}${trackableProgramName}`,
        type: 'click',
      },
    });
    const deeplink = `amextravel://hotelDetails?propertyID=${hotelId}&source=wishlist_ai`;
    window.location.href = deeplink;
  };

  const showPrice = !(totalPrice == null || totalPrice === '');

  return (
    <div className="position-relative width-full">
      <button
        type="button"
        className={`${styles.hotelCard} radius-container flex flex-column height-full width-full`}
        data-testid={`hotel-${hotelId}`}
        onClick={deeplinkTest}
      >
        <div
          className="
        flex flex-item-shrink flex-align-center radius-container-t
        "
          style={{ height: '200px', width: '100%' }}
        >
          <img
            src={url}
            className="radius-container-t"
            alt={title}
            style={{ objectFit: 'cover', height: '100%', width: '100%' }}
            data-testid="hotel-image"
          />
        </div>
        <div className={`
      pad-2 flex flex-column height-full ${styles.textContainer}
      `}
        >
          {programName && (
            <div className={
              `body-xsmall-bold
          pad-1 margin-1-b text-align-center text-uppercase 
          ${getPromotionBackground(programName)}
          ${styles.promotionTile}
          `
            }
            >{programName === 'FHR' ? 'FINE HOTELS + RESORTS®' : programDescription}
            </div>
          )}
          <div className={`${styles.clamp2} ${styles.textPrimary} heading-4 font-weight-bold pad-3-r`}>
            {name}
          </div>
          <div className={`${styles.hotelCardGrayFont} body`}>
            {city}, {location}
          </div>
          {showPrice && (
            <>
              <div className="flex flex-item-grow flex-align-end">
                <div className="flex flex-justify-center">
                  <div className={`${styles.textPrimary} body pad-1-r`}>
                    {(totalPrice && formatCurrency(totalPrice)) || 'error'}
                  </div>
                  <div className={`${styles.hotelCardGrayFont} body`}>
                    {intl.formatMessage({ id: 'hotelCard.fees' })}
                  </div>
                </div>
              </div>
              <div className={`${styles.hotelCardGrayFont} body-small`}>
                (including taxes and fees)
              </div>
            </>
          )}
        </div>
      </button>
      <div
        className={styles.checkboxContainer}
      >
        <button
          data-testid={`checkbox-${hotelId}`}
          type="button"
          className={styles.selectHotelButton}
          onClick={(e) => {
            e.stopPropagation();
            selectHotel(hotel);
          }}
        >
          {isSelected ? (
            <IconCheck
              data-testid="icon-check"
              color="brand"
              size="sm"
              isFilled={true}
              title={`Hotel ${name} is selected`}
              titleId={`selectedHotel-${hotelId}`}
            />
          ) : (
            <IconPlus
              data-testid="icon-plus"
              color="brand"
              size="sm"
              isFilled={true}
              title={`Select Hotel ${name}`}
              titleId={`selectedHotel-${hotelId}`}
            />
          )}
        </button>
      </div>
    </div>
  );
};

HotelCard.propTypes = {
  hotel: PropTypes.shape({
    hotelId: PropTypes.string,
    address: PropTypes.string,
    name: PropTypes.string,
    programRate: PropTypes.shape({
      programName: PropTypes.string,
      programDescription: PropTypes.string,
    }),
    media: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    })),
    totalPrice: PropTypes.number,
  }),
  isSelected: PropTypes.bool.isRequired,
  selectHotel: PropTypes.func.isRequired,
};
